import i18n from 'client/i18n'
import { createContainerStructure } from 'client/utils/createStructureUtils'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import {
  EntityWithChildIdsInterface,
  EntityWithChildIdsMigrationType,
} from 'common/types/entities/EntityInterface'
import { FieldInterface } from 'common/types/entities/field-interface'
import { createBookingCalendar } from 'src/shared/entities/booking-calendar/create-booking-calendar'
import { createField } from 'src/shared/entities/field/create-field'
import { createHeadline } from 'src/shared/entities/headline/create-headline'
import { createOfferPrice } from 'src/shared/entities/offer-price/create-offer-price'
import { createPaymentMethod } from 'src/shared/entities/payment-method/create-payment-method'
import { createRowColumnLayout } from 'src/shared/entities/row/create-row-column-layout'
import { createText } from 'src/shared/entities/text/create-text'
import { insertChild } from 'src/shared/entities/utils/generate-base-entity'

export const createPaidCalendarFromLibrary = (
  container: EntityWithChildIdsMigrationType | EntityWithChildIdsInterface,
  locale: string,
  position = 0,
) => {
  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )
  const fixedT = i18n.getFixedT(locale)

  const paidCalendarEntity = createBookingCalendar(
    descendantEntity.id,
    descendantEntity.masterBlockId,
    EntityTypeEnum.BookingPaidCalendar,
  )

  paidCalendarEntity.calendarStepHeadline = fixedT(
    paidCalendarEntity.calendarStepHeadline!,
  )
  paidCalendarEntity.calendarStepSubHeadline = fixedT(
    paidCalendarEntity.calendarStepSubHeadline!,
  )
  paidCalendarEntity.formStepHeadline = fixedT(
    paidCalendarEntity.formStepHeadline!,
  )
  paidCalendarEntity.formStepSubHeadline = fixedT(
    paidCalendarEntity.formStepSubHeadline!,
  )
  paidCalendarEntity.nextStepButtonText = fixedT(
    paidCalendarEntity.nextStepButtonText!,
  )
  paidCalendarEntity.formButtonText = fixedT(paidCalendarEntity.formButtonText!)
  if ('thankYouStepButtonText' in paidCalendarEntity) {
    paidCalendarEntity.thankYouStepButtonText = fixedT(
      paidCalendarEntity.thankYouStepButtonText!,
    )
  }

  const { row, column1 } = createRowColumnLayout({
    parentEntity: paidCalendarEntity,
    position,
  })

  const formTitle = createHeadline(
    column1.id,
    fixedT('entities.text_headline.default_text'),
    column1.masterBlockId,
  )

  const formDescription = createText(
    column1.id,
    column1.masterBlockId,
    fixedT('entities.calendar.form_description_default_text'),
  )
  formDescription.textAlign = 'left'
  formDescription.margin = {
    marginBottom: 20,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
  }

  const firstNameLabel = createText(
    column1.id,
    column1.masterBlockId,
    fixedT('entities.field.first_name.default_placeholder'),
  )
  firstNameLabel.textAlign = 'left'
  const lastNameLabel = createText(
    column1.id,
    column1.masterBlockId,
    fixedT('entities.field.last_name.default_placeholder'),
  )
  lastNameLabel.textAlign = 'left'
  const emailLabel = createText(
    column1.id,
    column1.masterBlockId,
    fixedT('entity_settings.email.title'),
  )
  emailLabel.textAlign = 'left'
  const phoneLabel = createText(
    column1.id,
    column1.masterBlockId,
    fixedT('entity_settings.phone.title'),
  )
  phoneLabel.textAlign = 'left'

  const resetTopMargins = (field: FieldInterface) => {
    field.margin = { ...field.margin, marginTop: 0 }
    field.mobileMargin = { ...field.mobileMargin, marginTop: 0 }
  }
  const firstNameField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.FirstName,
    placeholder: fixedT('entities.field.first_name.default_placeholder'),
  })
  resetTopMargins(firstNameField)

  const lastNameField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.LastName,
    placeholder: fixedT('entities.field.last_name.default_placeholder'),
  })
  resetTopMargins(lastNameField)

  const phoneField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.PhoneNumber,
    placeholder: fixedT('entity_settings.phone.title'),
  })
  resetTopMargins(phoneField)
  phoneField.optional = true

  const emailField = createField(column1.id, column1.masterBlockId, {
    slug: FieldSlugEnum.Email,
    placeholder: fixedT('entities.field.email.default_placeholder'),
  })
  resetTopMargins(emailField)

  const offerPrice = createOfferPrice(column1.id, column1.masterBlockId)

  const customerTypeEntity = createPaymentMethod(
    column1.id,
    column1.masterBlockId,
  )
  offerPrice.margin = { ...offerPrice.margin, marginTop: 10 }
  customerTypeEntity.margin = { ...customerTypeEntity.margin, marginTop: 10 }

  column1.childIds = [
    formTitle.id,
    formDescription.id,
    firstNameLabel.id,
    firstNameField.id,
    lastNameLabel.id,
    lastNameField.id,
    emailLabel.id,
    emailField.id,
    phoneLabel.id,
    phoneField.id,
    offerPrice.id,
    customerTypeEntity.id,
  ]
  row.childIds = [column1.id]

  paidCalendarEntity.childIds = [row.id]

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        paidCalendarEntity.id,
        position,
      ),
    },
    [paidCalendarEntity.id]: paidCalendarEntity,

    [row.id]: row,
    [column1.id]: column1,
    [formTitle.id]: formTitle,
    [formDescription.id]: formDescription,
    [firstNameLabel.id]: firstNameLabel,
    [firstNameField.id]: firstNameField,
    [lastNameLabel.id]: lastNameLabel,
    [lastNameField.id]: lastNameField,
    [phoneLabel.id]: phoneLabel,
    [phoneField.id]: phoneField,
    [emailLabel.id]: emailLabel,
    [emailField.id]: emailField,
    [offerPrice.id]: offerPrice,
    [customerTypeEntity.id]: customerTypeEntity,
  }
}
